// Failure screenshots for a login, grouped by run (newer API lists them under the login's own
// desk scope, so desks 1 and 2 show too; an older one sends a flat list, grouped here by the run
// folder in the path). `shot` opens one screenshot on its own.

import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Typography } from '@mui/material';
import { getScreenshots } from './api';
import { fmtDay, fmtTime, groupShots } from './deskModel';
import { useTones } from './tones';

const Shot = ({ s, tz }) => {
    const { muted } = useTones();
    return (
        <Box sx={{ mb: 2.5 }}>
            <Typography className="d-font" sx={{ fontSize: 12, color: muted, mb: 0.5 }}>
                {s.last_modified ? `${fmtTime(s.last_modified, tz)} · ` : ''}{String(s.name).split('/').pop()}{' '}
                <Link href={s.url} target="_blank" rel="noopener noreferrer">open full size</Link>
            </Typography>
            <Box component="img" src={s.url} alt={s.name} sx={{ maxWidth: '100%', border: '1px solid var(--color-border, #e0e0e0)' }} />
        </Box>
    );
};

// target: { login, shot? } or null.
const ScreenshotsDialog = ({ target, onClose }) => {
    const { muted } = useTones();
    const login = target?.login;
    const [groups, setGroups] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (!login || target?.shot) return;
        setGroups(null); setError(null);
        getScreenshots(login.id).then((d) => setGroups(groupShots(d))).catch((e) => setError(e.message));
    }, [login, target]);
    return (
        <Dialog open={Boolean(target)} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle className="d-font">
                {target?.shot ? String(target.shot.name).split('/').pop() : `Failure screenshots — ${login?.id}`}
            </DialogTitle>
            <DialogContent>
                {target?.shot ? <Shot s={target.shot} tz={login?.timezone} /> : (
                    <>
                        {error && <Alert severity="error">{error}</Alert>}
                        {!error && !groups && <CircularProgress size={24} />}
                        {groups && groups.length === 0 && (
                            <Typography className="d-font" sx={{ fontSize: 13, color: muted }}>
                                No failures with screenshots in the last two weeks.
                            </Typography>
                        )}
                        {groups && groups.map((g) => (
                            <Box key={`${g.day}-${g.run_id}`} sx={{ mb: 1 }}>
                                <Typography className="d-font" sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>
                                    {g.day ? fmtDay(g.day) : 'Earlier'}{g.run_id ? ` · run ${String(g.run_id).slice(0, 8)}` : ''}
                                </Typography>
                                {g.files.map((s) => <Shot key={s.name} s={s} tz={login?.timezone} />)}
                            </Box>
                        ))}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ '& .MuiButton-root': { textTransform: 'none' } }}><Button onClick={onClose}>Close</Button></DialogActions>
        </Dialog>
    );
};

export default ScreenshotsDialog;
